import { all, call, put, takeLatest } from 'redux-saga/effects';
import * as actions from './actions';
import * as actionsNotification from '../InitComponent/actions';
import { LoyaltyService } from './service';
import { ReceiptsResponseData, LoyaltyPageState } from './types';
import { validationCodeErrorHandler } from '../LoginPage/helpers';
import { formatReceipt } from './helpers';
import { NOTIFICATION_REPORT_ERROR } from '../../global/constants';

interface GetClientBalanceSagaProps {
  type: string;
  payload: any;
}

interface AapplyBonusesSagaProps {
  type: string;
  payload: string;
}

interface SubmitLoyaltyOrderSagaProps {
  type: string;
  payload: {
    phone: string;
    bonus_used: boolean;
    doc_id: number;
    source_code: string;
    items: { were_id: string; price: number; quantity: number; name: string }[];
    client_bonus_debit: number;
    client_bonus_credit: number;
    pharmacist_bonus_credit: number;
    sms_code?: string;
    postEffect?: () => void;
  };
}

interface GenerateLoyaltyReportParam {
  type: string;
  payload: {
    data: {
      from: string;
      to: string;
      integration_code: string;
      type: string;
      source_code?: string;
      phone?: string;
    };
    pdf: boolean;
    xls: boolean;
    postEffect: any;
  };
}

export function* getCurrentReceiptSaga() {
  try {
    const res: ReceiptsResponseData = yield call(
      LoyaltyService.getCurrentReceipt
    );
    const formattedReceipt = formatReceipt(res);
    yield put(actions.getCurrentReceipt.success(formattedReceipt));
  } catch (error) {
    yield put(actions.getCurrentReceipt.error(error));
  }
}

export function* getClientInfoSaga({ payload }: GetClientBalanceSagaProps) {
  try {
    const res: {
      result: LoyaltyPageState['clientInfo']['result'];
      status: string;
    } = yield call(LoyaltyService.getClientInfo, payload.phone);
    yield put(actions.getClientInfo.success(res?.result));
  } catch (error) {
    yield put(actions.getClientInfo.error(error));
  }
}

export function* applyBonusesSaga({ payload }: AapplyBonusesSagaProps) {
  try {
    const res: {
      status: string;
    } = yield call(LoyaltyService.applyBonuses, payload);
    yield put(actions.applyBonuses.success(res?.status));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };

    yield put(
      actions.applyBonuses.error(
        validationCodeErrorHandler(
          response.data.error,
          response.status.toString()
        )
      )
    );
  }
}

export function* submitLoyaltyOrderSaga({
  payload,
}: SubmitLoyaltyOrderSagaProps) {
  try {
    const res: {
      status: string;
      docId: string;
    } = yield call(LoyaltyService.submitLoyaltyOrder, payload);
    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.submitLoyaltyOrder.success(res?.docId));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };

    yield put(
      actions.submitLoyaltyOrder.error(
        validationCodeErrorHandler(
          response.data.error,
          response.status.toString()
        )
      )
    );
  }
}

export function* generateLoyaltyReportSaga({
  payload,
}: GenerateLoyaltyReportParam) {
  try {
    const response: { status: string; result: string } = yield call(
      LoyaltyService.generateLoyaltyReport,
      payload.data,
      payload.pdf,
      payload.xls
    );

    if (payload.postEffect) {
      yield call(payload.postEffect);
    }
    yield put(actions.generateLoyaltyReport.success(response.result));
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_REPORT_ERROR,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(actions.generateLoyaltyReport.error(error));
  }
}

export function* loyaltyPageWatcherSaga() {
  yield all([
    takeLatest(actions.getCurrentReceipt.REQUEST, getCurrentReceiptSaga),
    takeLatest(actions.getClientInfo.REQUEST, getClientInfoSaga),
    takeLatest(actions.applyBonuses.REQUEST, applyBonusesSaga),
    takeLatest(actions.submitLoyaltyOrder.REQUEST, submitLoyaltyOrderSaga),
    takeLatest(
      actions.generateLoyaltyReport.REQUEST,
      generateLoyaltyReportSaga
    ),
  ]);
}
